// Project-scoped "Induction Records" — completed Inductions + delivered Toolbox
// Talks (register, with attendee list) for ONE project. Used on both the
// client-portal Project view (PortalProjectView.jsx, next to "On Site") and the
// internal Project Detail page (ProjectDetailPage.jsx, Documents tab group).
// Each row offers the actual submitted evidence three ways: preview the PDF
// in-place, download it, or (inductions only) open the full editable form.
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    ShieldCheck, Users, Search, CalendarDays, ChevronDown, ChevronUp, ArrowDownUp, RefreshCw,
    Camera, FileCheck2, PenLine, Clock, Eye, Download, ExternalLink, X, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import Pill from "@/components/Pill";

const TYPE_TABS = [{ k: "", l: "All" }, { k: "induction", l: "Inductions" }, { k: "toolbox", l: "Toolbox Talks" }];

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return s; }
}

export default function ProjectInductionRecords({ projectId, brand = "var(--brand-accent,#C75A1B)" }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState("");
    const [q, setQ] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sortDir, setSortDir] = useState("desc");
    const [expanded, setExpanded] = useState(new Set());
    const [preview, setPreview] = useState(null);   // {title, fetchPdf, filename} | null

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ project_id: projectId });
        if (type) params.set("type", type);
        if (q.trim()) params.set("q", q.trim());
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        api.get(`/inductions/project-records?${params.toString()}`)
            .then((r) => setRecords(r.data || [])).catch(() => setRecords([])).finally(() => setLoading(false));
    }, [projectId, type, q, dateFrom, dateTo]);

    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const sorted = [...records].sort((a, b) => sortDir === "desc"
        ? (b.date || "").localeCompare(a.date || "")
        : (a.date || "").localeCompare(b.date || ""));
    const inductionCount = records.filter((r) => r.type === "induction").length;
    const toolboxCount = records.filter((r) => r.type === "toolbox").length;

    const toggleExpand = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const openForm = (r) => {
        const back = `${location.pathname}${location.search}`;
        navigate(`/app/inspections/${r.id}?back=${encodeURIComponent(back)}`);
    };
    const previewPdf = (r) => {
        if (r.type === "induction") {
            setPreview({
                title: r.person_name ? `Induction — ${r.person_name}` : r.title,
                filename: `${(r.person_name || "induction").replace(/\s+/g, "-")}.pdf`,
                fetchPdf: () => api.get(`/forms/instances/${r.id}/pdf`, { responseType: "blob", timeout: 180000 }),
            });
        } else if (r.document_id) {
            setPreview({
                title: r.title,
                filename: `${(r.title || "toolbox-talk").replace(/\s+/g, "-")}.pdf`,
                fetchPdf: () => api.get(`/documents/${r.document_id}/download`, { responseType: "blob" }),
            });
        }
    };
    const hasEvidence = (r) => r.type === "induction" || !!r.document_id;

    return (
        <div data-testid="induction-records-panel" className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center gap-2 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                <ShieldCheck className="w-4 h-4" style={{ color: brand }} />
                <h3 className="text-[13.5px] font-semibold text-[#1F1F1F] flex-1">Induction Records</h3>
                <span className="text-[11px] font-medium" style={{ color: "#9A968F" }}>
                    {inductionCount} induction{inductionCount === 1 ? "" : "s"} · {toolboxCount} toolbox talk{toolboxCount === 1 ? "" : "s"}
                </span>
                <button data-testid="induction-records-refresh" onClick={load} className="text-[#9A968F] hover:text-[#1F1F1F]" title="Refresh">
                    <RefreshCw className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="px-5 py-3 flex items-center gap-2 flex-wrap border-b" style={{ borderColor: "#F0EDE8", background: "#FCFBF9" }}>
                <div className="flex items-center gap-1">
                    {TYPE_TABS.map((t) => (
                        <button key={t.k || "all"} data-testid={`induction-records-type-${t.k || "all"}`} onClick={() => setType(t.k)}
                            className="px-3 py-1.5 rounded-full text-[11.5px] font-semibold border transition-colors"
                            style={type === t.k ? { background: brand, color: "#fff", borderColor: brand } : { borderColor: "#E5E0DA", color: "#6F6A64", background: "#fff" }}>
                            {t.l}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 min-w-[160px]">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#9A968F" }} />
                    <input data-testid="induction-records-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name or company…"
                        className="w-full h-8 pl-8 pr-2.5 rounded-md border text-[12px] bg-white outline-none" style={{ borderColor: "#E5E0DA" }} />
                </div>
                <input type="date" data-testid="induction-records-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    title="From date" className="h-8 px-2 rounded-md border text-[11.5px] bg-white outline-none" style={{ borderColor: "#E5E0DA" }} />
                <input type="date" data-testid="induction-records-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    title="To date" className="h-8 px-2 rounded-md border text-[11.5px] bg-white outline-none" style={{ borderColor: "#E5E0DA" }} />
                <button data-testid="induction-records-sort-toggle" onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                    className="h-8 px-2.5 rounded-md border text-[11.5px] font-semibold inline-flex items-center gap-1 bg-white" style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                    <ArrowDownUp className="w-3.5 h-3.5" /> {sortDir === "desc" ? "Newest first" : "Oldest first"}
                </button>
            </div>

            {loading ? (
                <div className="px-5 py-8 text-center text-[12px]" style={{ color: "#9A968F" }}>Loading…</div>
            ) : sorted.length === 0 ? (
                <div className="px-5 py-10 text-center text-[12px]" style={{ color: "#9A968F" }} data-testid="induction-records-empty">
                    No induction or toolbox talk records for this project yet.
                </div>
            ) : (
                <ul data-testid="induction-records-list">
                    {sorted.map((r) => (
                        <li key={r.id} data-testid={`induction-record-row-${r.id}`}
                            className="px-5 py-3 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0" style={{ background: r.type === "toolbox" ? "#EAF1FB" : "#FBEFE5" }}>
                                    {r.type === "toolbox"
                                        ? <Users className="w-4 h-4" style={{ color: "#2A5DB0" }} />
                                        : <ShieldCheck className="w-4 h-4" style={{ color: brand }} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">
                                        {r.type === "toolbox" ? r.title : (r.person_name || "—")}
                                    </div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5 flex items-center gap-1.5 flex-wrap">
                                        {r.type === "toolbox" ? (
                                            <button data-testid={`induction-record-expand-${r.id}`}
                                                onClick={() => toggleExpand(r.id)}
                                                className="inline-flex items-center gap-1 font-semibold" style={{ color: brand }}>
                                                {r.attendee_count} attendee{r.attendee_count === 1 ? "" : "s"}
                                                {expanded.has(r.id) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                            </button>
                                        ) : (r.company || "—")}
                                        {r.site_name && <span>· {r.site_name}</span>}
                                        {r.type === "induction" && (r.photo_count > 0 || r.certificate_count > 0 || r.has_signature) && (
                                            <span className="inline-flex items-center gap-2.5 ml-1">
                                                {r.has_signature && <span className="inline-flex items-center gap-0.5"><PenLine className="w-3 h-3" />Signed</span>}
                                                {r.photo_count > 0 && <span className="inline-flex items-center gap-0.5"><Camera className="w-3 h-3" />{r.photo_count}</span>}
                                                {r.certificate_count > 0 && <span className="inline-flex items-center gap-0.5"><FileCheck2 className="w-3 h-3" />{r.certificate_count}</span>}
                                            </span>
                                        )}
                                    </div>
                                    {r.type === "toolbox" && expanded.has(r.id) && (
                                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`induction-record-attendees-${r.id}`}>
                                            {r.attendees.map((a, i) => (
                                                <span key={i} className="text-[10.5px] px-2 py-1 rounded-full" style={{ background: "#F0EDE8", color: "#3e4b63" }}>
                                                    {a.name}{a.company ? ` · ${a.company}` : ""}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                    <span className="text-[10.5px] inline-flex items-center gap-1" style={{ color: "#9A968F" }}>
                                        <CalendarDays className="w-3 h-3" />{fmtDate(r.date)}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <Pill tone={r.status === "Approved" ? "green" : r.status === "Draft" ? "neutral" : "blue"}>{r.status}</Pill>
                                        {r.type === "induction" && r.evidence_status && (
                                            <Pill tone={r.evidence_status === "outstanding" ? "amber" : "green"}>
                                                {r.evidence_status === "outstanding" ? "Evidence outstanding" : "Evidence complete"}
                                            </Pill>
                                        )}
                                        {r.type === "toolbox" && !r.register_closed && (
                                            <Pill tone="neutral" icon={Clock}>Register open</Pill>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0 border-l pl-3" style={{ borderColor: "#F0EDE8" }}>
                                    {hasEvidence(r) ? (
                                        <>
                                            <button data-testid={`induction-record-preview-${r.id}`} onClick={() => previewPdf(r)} title="Preview PDF"
                                                className="w-7 h-7 grid place-items-center rounded-lg hover:bg-[#F6F4F0]" style={{ color: brand }}>
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                            <button data-testid={`induction-record-download-${r.id}`}
                                                onClick={async () => {
                                                    const fetchPdf = r.type === "induction"
                                                        ? () => api.get(`/forms/instances/${r.id}/pdf`, { responseType: "blob", timeout: 180000 })
                                                        : () => api.get(`/documents/${r.document_id}/download`, { responseType: "blob" });
                                                    const res = await fetchPdf();
                                                    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                                    const a = document.createElement("a");
                                                    a.href = url; a.download = `${(r.type === "induction" ? r.person_name : r.title) || "record"}.pdf`; a.click();
                                                    URL.revokeObjectURL(url);
                                                }}
                                                title="Download PDF" className="w-7 h-7 grid place-items-center rounded-lg hover:bg-[#F6F4F0]" style={{ color: "#6F6A64" }}>
                                                <Download className="w-3.5 h-3.5" />
                                            </button>
                                        </>
                                    ) : <span className="w-14" />}
                                    {r.type === "induction" && (
                                        <button data-testid={`induction-record-openform-${r.id}`} onClick={() => openForm(r)} title="Open form"
                                            className="w-7 h-7 grid place-items-center rounded-lg hover:bg-[#F6F4F0]" style={{ color: "#6F6A64" }}>
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {preview && <PdfPreviewDialog {...preview} brand={brand} onClose={() => setPreview(null)} />}
        </div>
    );
}

function PdfPreviewDialog({ title, filename, fetchPdf, brand, onClose }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let objUrl = null, cancelled = false;
        (async () => {
            try {
                const res = await fetchPdf();
                if (cancelled) return;
                objUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                setUrl(objUrl);
            } catch {
                if (!cancelled) setError("Could not load the PDF. Please try again.");
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
    }, [fetchPdf]);

    const download = () => {
        if (!url) return;
        const a = document.createElement("a");
        a.href = url; a.download = filename || "record.pdf"; a.click();
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" data-testid="instance-pdf-preview-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col" style={{ height: "90vh" }}>
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-[15px] font-bold text-[#1F1F1F] truncate">{title}</h3>
                    <div className="flex items-center gap-2">
                        <button onClick={download} disabled={!url} data-testid="instance-pdf-preview-download"
                            className="inline-flex items-center gap-1 text-[13px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-50" style={{ background: brand }}>
                            <Download size={13} /> Download
                        </button>
                        <button onClick={onClose} data-testid="instance-pdf-preview-close" className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                    </div>
                </div>
                <div className="flex-1 bg-[#525659] min-h-0 grid place-items-center">
                    {loading ? (
                        <div className="text-white/80 text-sm inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Generating PDF… this can take a moment.</div>
                    ) : error ? (
                        <div className="text-white/80 text-sm text-center px-6">{error}</div>
                    ) : (
                        <iframe title={title} src={url} className="w-full h-full bg-white" data-testid="instance-pdf-preview-frame" />
                    )}
                </div>
            </div>
        </div>
    );
}
